import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { CtaBand, FaqList, PageHero } from "@/components/page-parts";
import { FIRM, FIRM_ADDRESS, MICHAEL_PHONE, PENINSULA_ADDRESS, faqSchema } from "@/lib/firm";

const SITE = "https://coastsidelaw.com.au";
const URL = `${SITE}/will-capacity-faqs`;

const TITLE = "Will Capacity, Challenges & Uncertain Date of Death — QLD FAQs";
const DESCRIPTION = `Plain-English answers on testamentary capacity at signing, when a will can be challenged in Queensland, and what happens to probate if the date of death is unclear. Call ${FIRM.phone}.`;

type Faq = { q: string; a: string };

const CAPACITY: Faq[] = [
  {
    q: "What does 'capacity' to make a will mean?",
    a: "The will-maker must understand they are making a will and what it does, have a general idea of what they own, know who might reasonably expect to benefit, and not have their decisions distorted by a disorder of the mind. This is the Banks v Goodfellow test, which Queensland courts still apply.",
  },
  {
    q: "Can someone with dementia make a valid will?",
    a: "Yes, sometimes. A diagnosis alone does not remove capacity. What matters is the person's understanding at the time they give instructions and sign. Early-stage dementia and good and bad days are common, so evidence from the day itself carries the most weight.",
  },
  {
    q: "At what moment is capacity tested?",
    a: "When the will is signed. If the person had capacity when they gave instructions and the will was prepared exactly as instructed, the court may still uphold it even if their capacity had slipped by the signing day, provided they understood they were signing the will they had asked for.",
  },
  {
    q: "What should a solicitor do if capacity may be in doubt?",
    a: "See the person alone, ask open questions, keep detailed notes in their own words, and where there is real doubt obtain a doctor's assessment close to signing. Those records are what the court relies on years later if the will is questioned.",
  },
  {
    q: "Why would the court send a requisition about capacity?",
    a: "Usually because the will was signed shortly before death, or the cause of death on the certificate (such as dementia, a brain tumour or delirium) suggests the mind may have been affected. The executor then needs affidavit evidence showing capacity at signing.",
  },
];

const CHALLENGES: Faq[] = [
  {
    q: "On what grounds can a will be challenged in Queensland?",
    a: "The main grounds are lack of testamentary capacity, the will-maker not knowing and approving of the contents, undue influence (coercion, not mere persuasion), fraud or forgery, and the will not being signed and witnessed properly. Separately, eligible family members can make a family provision claim even if the will is valid.",
  },
  {
    q: "Who can challenge a will?",
    a: "Anyone with an interest in the estate can challenge its validity, typically a beneficiary under an earlier will or a person who would inherit if there were no will. A family provision claim is limited to a spouse or de facto partner, children (including stepchildren in some cases) and certain dependants.",
  },
  {
    q: "Are there time limits?",
    a: "Yes. For a family provision claim in Queensland, written notice must be given to the executor within six months of death and court proceedings started within nine months. Validity challenges should be raised as early as possible, ideally by lodging a caveat before probate is granted.",
  },
  {
    q: "Who proves capacity if the will is challenged?",
    a: "If the will looks rational and was properly signed, capacity is presumed. Once the challenger raises a real doubt, the burden moves to the person relying on the will to prove the will-maker had capacity. That is why records made at signing matter so much.",
  },
  {
    q: "What happens if a will is found invalid?",
    a: "The court looks to the previous valid will. If there is none, the estate passes under Queensland's intestacy rules, which set out a fixed order of spouse, children and other relatives.",
  },
];

const DATE_OF_DEATH: Faq[] = [
  {
    q: "What if the death certificate gives a range of dates?",
    a: "Where a person is found some time after they died, the certificate may say 'on or about' a date, or 'between' two dates. Probate can still be granted, but the Registrar may ask for affidavit evidence, for example when the person was last seen or spoken to, police reports and the coroner's findings.",
  },
  {
    q: "Why does the exact date matter?",
    a: "It can decide whether the will-maker was alive when a will or codicil was signed, whether a beneficiary survived them, and which assets and debts form part of the estate. It also affects time limits such as the family provision deadlines.",
  },
  {
    q: "What if two people die together and it is unclear who died first?",
    a: "Under the Succession Act 1981 (Qld), where the order of deaths cannot be established, the younger person is generally presumed to have survived the elder for the purpose of dealing with property. Many wills also include a clause requiring a beneficiary to survive the will-maker by 30 days, which usually resolves the question.",
  },
  {
    q: "What if a person is missing and there is no body?",
    a: "An executor can apply to the Supreme Court for a grant on the basis that the person is presumed dead. The court needs evidence of the circumstances, the searches made and that the person has not been heard of, commonly for seven years or more, although a shorter period may be accepted where death is clearly likely, such as a disappearance at sea.",
  },
  {
    q: "Can Coastside Law help with an uncertain date of death?",
    a: "Yes. We prepare the evidence the Registrar needs, deal with the coroner and police records, and apply for the grant, including presumption of death applications.",
  },
];

const ALL = [...CAPACITY, ...CHALLENGES, ...DATE_OF_DEATH];

const GROUPS = [
  { id: "capacity", heading: "Capacity at signing", items: CAPACITY },
  { id: "challenges", heading: "When a will can be challenged", items: CHALLENGES },
  { id: "date-of-death", heading: "When the date of death is unclear", items: DATE_OF_DEATH },
];

export const Route = createFileRoute("/will-capacity-faqs")({
  head: () => ({
    meta: [
      { title: fitTitle(TITLE) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      { property: "og:title", content: fitTitle(TITLE) },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(ALL)) }],
  }),
  component: WillCapacityFaqs,
});

function WillCapacityFaqs() {
  return (
    <>
      <PageHero
        eyebrow="Wills & estates FAQs"
        title="Capacity, will challenges and uncertain dates of death"
        lead="Straight answers to the questions executors and families ask us when a will or a death raises doubts."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <nav className="flex flex-wrap gap-2">
          {GROUPS.map((g) => (
            <a
              key={g.id}
              href={`#${g.id}`}
              className="rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:border-primary"
            >
              {g.heading}
            </a>
          ))}
        </nav>

        {GROUPS.map((g) => (
          <div key={g.id} id={g.id} className="mt-14 scroll-mt-28">
            <h2 className="font-serif text-3xl text-foreground">{g.heading}</h2>
            <div className="mt-6">
              <FaqList items={g.items} />
            </div>
          </div>
        ))}

        <h2 className="mt-14 font-serif text-3xl text-foreground">Read more</h2>
        <ul className="mt-5 grid gap-3">
          <li>
            <Link
              to="/guides/$slug"
              params={{ slug: "probate-requisition-capacity-queensland" }}
              className="block rounded-sm border border-border bg-card p-5 transition-colors hover:border-primary"
            >
              <span className="font-serif text-lg text-foreground">
                When the court requisitions a probate application over capacity
              </span>
            </Link>
          </li>
          <li>
            <Link
              to="/probate-checklist-queensland"
              className="block rounded-sm border border-border bg-card p-5 transition-colors hover:border-primary"
            >
              <span className="font-serif text-lg text-foreground">
                Probate checklist for executors
              </span>
            </Link>
          </li>
          <li>
            <Link
              to="/estate-disputes"
              className="block rounded-sm border border-border bg-card p-5 transition-colors hover:border-primary"
            >
              <span className="font-serif text-lg text-foreground">Estate disputes</span>
            </Link>
          </li>
        </ul>

        <p className="mt-10 text-sm leading-relaxed text-muted-foreground">
          Coastside Law, {FIRM_ADDRESS} and {PENINSULA_ADDRESS}. Call {FIRM.phone} or{" "}
          {MICHAEL_PHONE} (Michael Klein). General information about Queensland law, not advice on
          your matter.
        </p>

        <div className="mt-12">
          <EnquiryForm heading="Ask us about a will or estate" />
        </div>
      </section>

      <CtaBand
        title="Worried a will may be questioned?"
        body="Talk to us early. The right evidence gathered now saves time and cost later."
      />
    </>
  );
}
